import { type ReactElement, useId } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { getMessage } from '@/shared/copy/messages';
import { formErrorAlertSx } from '@/shared/ui/formAlertStyles';
import { s } from '@/theme/tokens';
import { uiToneSeverityMap } from '@/theme/uiTokens';
import { Box, Panel, Stack, Text } from '@/ui';
import {
  Alert,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  VisibilityIcon,
  VisibilityOffIcon,
} from '@/ui/primitives';
import { passwordIconButtonSx } from './userSettingsStyles';
import type { PasswordChangeFormState } from '@/features/users/hooks/usePasswordChangeForm';

type PasswordChangeFormProps = { password: PasswordChangeFormState };

export const PasswordChangeForm = ({ password }: PasswordChangeFormProps): ReactElement => {
  const currentPasswordId = useId();
  const newPasswordId = useId();
  const confirmPasswordId = useId();

  return (
    <Panel
      title="Change Password"
      actions={
        <Button
          variant="contained"
          color="primary"
          disableElevation
          fullWidthOnMobile={false}
          disabled={password.saveDisabled}
          aria-label="Change Password"
          onClick={password.onSave}
        >
          {getMessage('account.password.changeCta')}
        </Button>
      }
    >
      <Stack gap="md">
        <Box>
          <TextField
            id={currentPasswordId}
            label="Current Password"
            type={password.showCurrentPassword ? 'text' : 'password'}
            value={password.currentPassword}
            onChange={(event) => {
              password.onCurrentPasswordChange(event.target.value);
            }}
            fullWidth
            disabled={password.saving}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        password.showCurrentPassword
                          ? 'Hide current password'
                          : 'Show current password'
                      }
                      edge="end"
                      onClick={password.onToggleCurrentPassword}
                      sx={passwordIconButtonSx}
                    >
                      {password.showCurrentPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Box>
          <TextField
            id={newPasswordId}
            label="New Password"
            type={password.showNewPassword ? 'text' : 'password'}
            fullWidth
            value={password.newPassword}
            onChange={(event) => {
              password.onNewPasswordChange(event.target.value);
            }}
            disabled={password.saving}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        password.showNewPassword ? 'Hide new password' : 'Show new password'
                      }
                      edge="end"
                      onClick={password.onToggleNewPassword}
                      sx={passwordIconButtonSx}
                    >
                      {password.showNewPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Box>
          <TextField
            id={confirmPasswordId}
            label="Confirm New Password"
            type={password.showConfirmPassword ? 'text' : 'password'}
            fullWidth
            value={password.confirmPassword}
            onChange={(event) => {
              password.onConfirmPasswordChange(event.target.value);
            }}
            disabled={password.saving}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        password.showConfirmPassword
                          ? 'Hide confirm password'
                          : 'Show confirm password'
                      }
                      edge="end"
                      onClick={password.onToggleConfirmPassword}
                      sx={passwordIconButtonSx}
                    >
                      {password.showConfirmPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        {password.panelAlertMessage ? (
          <Alert
            severity={uiToneSeverityMap.red}
            color="error"
            variant="outlined"
            sx={[formErrorAlertSx, { mt: s('sm') }]}
          >
            {password.panelAlertMessage}
          </Alert>
        ) : null}
      </Stack>
      <ConfirmDialog
        open={password.reverificationDialogOpen}
        title={getMessage('account.password.reverifySessionTitle')}
        description={getMessage('account.password.reverifySessionDescription')}
        confirmLabel={getMessage('account.password.reverifyContinue')}
        confirmColor="primary"
        confirmDisabled={password.reverificationPassword.trim().length === 0}
        loading={password.reverificationSubmitting}
        onClose={password.onReverificationCancel}
        onConfirm={password.onReverificationSubmit}
      >
        <Stack gap="md">
          <TextField
            label="Current Password"
            type="password"
            value={password.reverificationPassword}
            onChange={(event) => {
              password.onReverificationPasswordChange(event.target.value);
            }}
            fullWidth
            disabled={password.reverificationSubmitting}
          />
          {password.reverificationError ? (
            <Text role="captionSmall" color="error">
              {password.reverificationError}
            </Text>
          ) : null}
        </Stack>
      </ConfirmDialog>
    </Panel>
  );
};
